import { Grid, GridItem } from "@chakra-ui/react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { FieldEditor } from "./FieldEditor";

const SortableField = ({ id, colSpan, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "default",
  };

  return (
    <GridItem ref={setNodeRef} colSpan={colSpan} style={style}>
      {children(listeners, attributes)}
    </GridItem>
  );
};

export const NestedDataGrid = ({
  tabKey,
  data,
  formState,
  onInputChange,
  onEdit,
  onDelete,
  onReorder,
  onAddNew,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const entries = Object.entries(data || {});
  const visibleFields = entries.filter(
    ([, value]) => value && value.MESSAGE && value.VISIBLE !== false
  );
  const hiddenFields = entries.filter(
    ([, value]) => value && value.MESSAGE && value.VISIBLE === false
  );
  const orderedFields = [...visibleFields, ...hiddenFields];

  // const handleAddNewField = () => {
  //   const existingKeys = orderedFields.map(([k]) => k);
  //   let counter = 1;
  //   let newKey = `NEW_FIELD_${counter}`;
  //   while (existingKeys.includes(newKey)) {
  //     counter++;
  //     newKey = `NEW_FIELD_${counter}`;
  //   }
  //   // Create default field structure
  //   const defaultField = {
  //     MESSAGE: "New Field",
  //     TYPE: "String",
  //     VALUE: "",
  //     EDITABLE: true,
  //     VISIBLE: true,
  //     TOOLTIP: "",
  //   };
  //   onAddNew?.(tabKey, newKey, defaultField);
  // };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={(event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = orderedFields.findIndex(([k]) => k === active.id);
        const newIndex = orderedFields.findIndex(([k]) => k === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const newOrder = [...orderedFields];
        const [moved] = newOrder.splice(oldIndex, 1);
        newOrder.splice(newIndex, 0, moved);

        onReorder?.(newOrder.map(([k, v]) => ({ key: k, value: v })));
      }}
    >
      <SortableContext
        items={orderedFields.map(([k]) => k)}
        strategy={rectSortingStrategy}
      >
        <Grid templateColumns="repeat(4, 1fr)" gap={2}>
          {orderedFields.map(([key, value]) => {
            if (!("MESSAGE" in value)) return null;

            const editable = value?.EDITABLE;
            const val = editable
              ? formState?.[key] ?? value.VALUE ?? ""
              : value.VALUE ?? "";
            const isTextBox = value.TYPE === "TextBox";

            return (
              <SortableField key={key} id={key} colSpan={isTextBox ? 4 : 1}>
                {(listeners, attributes) => (
                  <FieldEditor
                    label={value.MESSAGE}
                    code={value.REFERENCE?.CODE}
                    referenceMap={value.REFERENCE}
                    value={val}
                    tooltip={value.TOOLTIP}
                    isEditable={editable}
                    enumOptions={
                      value.TYPE === "Dropdown" ? value.ENUM : undefined
                    }
                    onChange={(newVal) => onInputChange(key, newVal)}
                    type={value.TYPE}
                    onEdit={() => onEdit(value, key)}
                    onDeleteItem={() => onDelete?.(key)}
                    isVisible={!("VISIBLE" in value) || value.VISIBLE !== false}
                    dragListeners={listeners}
                    dragAttributes={attributes}
                    isDragAble={true}
                    MULTISELECT_TYPE={value.MULTISELECT_TYPE}
                  />
                )}
              </SortableField>
            );
          })}

          {/* Add New Field Button
          <GridItem colSpan={1}>
            <Button
              onClick={handleAddNewField}
              variant="outline"
              borderStyle="dashed"
              borderWidth={2}
              width="100%"
              height="100%"
              minHeight="60px"
              colorScheme="blue"
              leftIcon={<Icon as={FaPlus} />}
              _hover={{
                bg: "blue.50",
                borderColor: "blue.400",
              }}
            >
              Add Field
            </Button>
          </GridItem> */}
        </Grid>
      </SortableContext>
    </DndContext>
  );
};
